import { useState } from 'react';
import { GitBranch } from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { FlowDiagram } from '@/components/prioritization/system-flows/FlowDiagram';
import { itemLifecycleFlow } from '@/data/systemFlows/itemLifecycle';
import { cycleExecutionFlow } from '@/data/systemFlows/cycleExecution';
import type { SystemFlow } from '@/data/systemFlows/index';

const flows: SystemFlow[] = [itemLifecycleFlow, cycleExecutionFlow];

export default function SystemFlows() {
  const [selectedId, setSelectedId] = useState<string>(flows[0].id);

  const selected = flows.find((f) => f.id === selectedId) ?? flows[0];

  return (
    <div>
      <PageHeader
        icon={<GitBranch className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="System Flows"
        subtitle="Visual documentation of how the system's core processes work"
      />

      {/* Body */}
      <div className="flex gap-0 border border-[var(--rule)] overflow-hidden" style={{ height: 'calc(100vh - 220px)' }}>
        {/* Left sidebar */}
        <aside className="flex-shrink-0 w-52 border-r border-[var(--rule)] bg-[var(--surface)] overflow-y-auto">
          <nav className="p-3 space-y-1">
            {flows.map((flow) => {
              const isActive = flow.id === selectedId;
              return (
                <button
                  key={flow.id}
                  onClick={() => setSelectedId(flow.id)}
                  className={[
                    'w-full text-left px-3 py-3  transition-colors',
                    isActive ? 'bg-[var(--slate-dim)]' : 'hover:bg-[var(--hover-tint)]',
                  ].join(' ')}
                >
                  <p className={['font-medium text-sm', isActive ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'].join(' ')}>
                    {flow.title}
                  </p>
                  <span className={[
                    'inline-block mt-1 px-2 py-0.5 rounded-full text-xs',
                    isActive ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
                  ].join(' ')}>
                    {flow.audience}
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main canvas — fixed height, ReactFlow fills it */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-shrink-0 px-5 py-3 border-b border-[var(--rule)] bg-[var(--bg-raised)]">
            <h2 className="text-base font-semibold text-[var(--text-pri)]">{selected.title}</h2>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">{selected.description}</p>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <FlowDiagram
              nodes={selected.nodes}
              edges={selected.edges}
              title={selected.title}
              description={selected.description}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
